/**
 * API Client with Firebase Authentication
 * Automatically adds Firebase ID token to requests for authenticated endpoints
 * Waits for Firebase Auth to initialize before making authenticated requests
 */

import { auth } from '../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { API_BASE_URL } from '../config';

/**
 * Firebase Auth initialization state
 * Resolves when Firebase Auth has finished initializing (restored session or confirmed no user)
 */
let authInitialized = false;
let authInitPromise: Promise<User | null> | null = null;

function waitForAuthInit(): Promise<User | null> {
  if (authInitialized) {
    return Promise.resolve(auth.currentUser);
  }
  
  if (!authInitPromise) {
    authInitPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        authInitialized = true;
        unsubscribe();
        resolve(user);
      });
    });
  }
  
  return authInitPromise;
}

/**
 * Get the current Firebase ID token
 * Waits for Firebase Auth to initialize first
 * Returns null if user is not authenticated
 */
export async function getFirebaseIdToken(): Promise<string | null> {
  // Wait for Firebase Auth to initialize (handles page refresh)
  const user = await waitForAuthInit();
  
  if (!user) {
    return null;
  }
  
  try {
    // Force refresh to ensure we have a valid token
    const token = await user.getIdToken(true);
    return token;
  } catch (error) {
    return null;
  }
}

/**
 * Make an authenticated API request
 * Automatically adds Firebase ID token to Authorization header
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getFirebaseIdToken();
  
  const headers = new Headers(options.headers);
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Ensure Content-Type is set for JSON requests
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * API methods for common operations
 */
export const apiClient = {
  /**
   * GET request with authentication
   */
  async get(endpoint: string, options: RequestInit = {}): Promise<Response> {
    return authenticatedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      ...options,
    });
  },

  /**
   * POST request with authentication
   */
  async post(endpoint: string, body?: any, options: RequestInit = {}): Promise<Response> {
    return authenticatedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  },

  /**
   * PUT request with authentication
   */
  async put(endpoint: string, body?: any, options: RequestInit = {}): Promise<Response> {
    return authenticatedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  },

  /**
   * DELETE request with authentication
   */
  async delete(endpoint: string, options: RequestInit = {}): Promise<Response> {
    return authenticatedFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      ...options,
    });
  },
};

export default apiClient;

